import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

import { config } from '~app/config';
import { AuthGuard, AccessGuard, ActivityGuard } from '~app/guards';
import { ApiProvider, PortalQueryClientProvider } from '~app/providers';

import { AccessDeniedPage } from '~pages/access-denied';
import { DashboardPage } from '~pages/dashboard';
import { LoansPage } from '~pages/loans';
import { LoginPage } from '~pages/login';
import { CreateOrderPage, OrdersPage } from '~pages/orders';
import { PaymentsPage } from '~pages/payments';
import { TransactionsPage } from '~pages/transactions';

import { logger } from '~entities/logger';

import { WebRequestClient } from '~shared/api/scp/client/web-request/web-request';
import { Header } from '~shared/ui/header/header';

import log from '@tinkoff/request-plugin-log';

import {
  initSentry,
  setUser,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  withSentryReactRouterV6Routing,
} from '@breeze/web-lib-sentry';
import { createWuid } from '@breeze/web-lib-wuid';
import { Notifications, NotificationsProvider } from '@pfa/front-notifications';

import './globals.css';
import './index.css';

const wuid = createWuid();

initSentry({
  dsn: import.meta.env.VITE_SENTRY_HOST,
  release: config.version,
  environment: config.environment,
  integrations: [
    reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    replayIntegration(),
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
setUser({ id: wuid });

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <ApiProvider
        config={config}
        scpClient={
          new WebRequestClient({
            plugins: [log({ name: 'client-api', logger })],
          })
        }
      >
        <NotificationsProvider>
          <Notifications offsetTop={20} reverse />
          <PortalQueryClientProvider>
            <SentryRoutes>
              <Route
                element={
                  <AuthGuard>
                    <Outlet />
                  </AuthGuard>
                }
              >
                <Route path="login" element={<LoginPage />} />
                <Route
                  element={
                    <AccessGuard>
                      <ActivityGuard>
                        <Header />
                        <Outlet />
                      </ActivityGuard>
                    </AccessGuard>
                  }
                >
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route
                    path=""
                    element={<Navigate to="dashboard" replace />}
                  />
                  <Route path="transactions" element={<TransactionsPage />}>
                    <Route path="loans" element={<LoansPage />} />
                    <Route path="payments" element={<PaymentsPage />} />
                  </Route>

                  <Route path="orders" element={<OrdersPage />} />
                  <Route path="orders/create" element={<CreateOrderPage />} />
                </Route>
                <Route path="access-denied" element={<AccessDeniedPage />} />
              </Route>
            </SentryRoutes>
          </PortalQueryClientProvider>
        </NotificationsProvider>
      </ApiProvider>
    </BrowserRouter>
  </React.StrictMode>
);
